import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Circle, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import { useAuth } from "@/context/AuthContext";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { MapPinOff, Navigation } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { TODAY, TrackPicker } from "@/components/TrackPicker";
import { api, errMsg, fmtDate } from "@/lib/api";
import { STATUS } from "@/lib/constants";
import { BRANCH_ZONES, DEFAULT_CENTER, STATUS_COLOR, agentIcon, hasCoords, inspectionIcon, isStale, rankAgents } from "@/lib/map";

const FitBounds = ({ points }) => {
  const map = useMap();
  const key = points.map((p) => p.join(",")).join("|");
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 13);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
};

const AssignBox = ({ insp, agents, onAssigned }) => {
  const ranked = rankAgents(agents, insp.customer).slice(0, 5);
  const assign = async (a) => {
    try {
      await api.put(`/inspections/${insp.id}`, { agent_id: a.id });
      toast.success(`تم الإسناد إلى ${a.name}`);
      onAssigned();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-2 space-y-1" data-testid={`map-assign-box-${insp.id}`}>
      <p className="text-xs font-bold text-slate-500">الأنسب للإسناد (الأقرب ثم الأقل ضغطاً)</p>
      {ranked.map((a) => (
        <button key={a.id} onClick={() => assign(a)} disabled={a.id === insp.agent_id} data-testid={`map-assign-${insp.id}-${a.id}`}
          className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md border border-slate-200 hover:bg-brand-mist disabled:opacity-50 text-xs">
          <span className="font-semibold">{a.name}{a.id === insp.agent_id && " (الحالي)"}</span>
          <span className="text-slate-500 font-mono">{a.km != null ? `${a.km.toFixed(1)} كم` : a.region || "-"} · {a.active_tasks} مهام</span>
        </button>
      ))}
    </div>
  );
};

const Legend = ({ filter, setFilter }) => (
  <div className="flex flex-wrap gap-1.5">
    {Object.entries(STATUS).map(([k, s]) => (
      <button key={k} onClick={() => setFilter((f) => ({ ...f, [k]: !f[k] }))} data-testid={`map-filter-${k}`}
        className={`h-9 px-3 rounded-full border text-sm font-semibold flex items-center gap-1.5 transition-opacity ${filter[k] ? "bg-white border-slate-300" : "opacity-40 border-slate-200"}`}>
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS_COLOR[k] }} />{s.label}
      </button>
    ))}
    <button onClick={() => setFilter((f) => ({ ...f, agents: !f.agents }))} data-testid="map-filter-agents"
      className={`h-9 px-3 rounded-full border text-sm font-semibold flex items-center gap-1.5 ${filter.agents ? "bg-white border-slate-300" : "opacity-40 border-slate-200"}`}>
      <span className="h-2.5 w-2.5 rounded-full bg-brand-red ring-2 ring-brand-navy" />المناديب
    </button>
  </div>
);

export default function MapPage() {
  const [data, setData] = useState({ inspections: [], agents: [] });
  const [filter, setFilter] = useState({ assigned: true, in_progress: true, on_hold: true, completed: false, agents: true });
  const { branches } = useAuth();
  const [track, setTrack] = useState(null);
  const showTrack = async (a, day = TODAY()) => {
    try {
      const { data: t } = await api.get(`/agents/${a.id}/track`, { params: { day } });
      if (!t.points.length) return toast.info(`لا توجد نقاط مسار لـ ${a.name} في ${day === TODAY() ? "اليوم" : day}`);
      setTrack({ id: a.id, day, name: t.agent, points: t.points.map((p) => [p.lat, p.lng]), from: t.points[0].at, to: t.points[t.points.length - 1].at, count: t.points.length });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const load = useCallback(() => api.get("/map").then((r) => setData(r.data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const visible = data.inspections.filter((i) => filter[i.status]);
  const placed = visible.filter((i) => hasCoords(i.customer));
  const unplaced = visible.filter((i) => !hasCoords(i.customer));
  const located = filter.agents ? data.agents.filter((a) => hasCoords(a.location)) : [];
  const points = useMemo(() => [...placed.map((i) => [i.customer.lat, i.customer.lng]), ...located.map((a) => [a.location.lat, a.location.lng])], [placed, located]);

  return (
    <div className="space-y-5" data-testid="map-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold text-brand-navy mb-1"><span className="font-mono tracking-widest">MAP</span> · تحديث تلقائي</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">خريطة المعاينات والمناديب</h1>
        </div>
        <Legend filter={filter} setFilter={setFilter} />
      </div>
      <div className="grid lg:grid-cols-4 gap-4">
        <div dir="ltr" className="lg:col-span-3 h-[65vh] rounded-xl overflow-hidden border border-slate-200 bg-white" data-testid="inspections-map">
          <MapContainer center={DEFAULT_CENTER} zoom={5} className="h-full w-full">
            <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={track ? track.points : points} />
            {branches.map((b) => BRANCH_ZONES[b] && (
              <Circle key={b} center={BRANCH_ZONES[b].center} radius={BRANCH_ZONES[b].radius}
                pathOptions={{ color: BRANCH_ZONES[b].color, weight: 2, fillOpacity: 0.05, dashArray: "6 6" }} />
            ))}
            {track && <Polyline positions={track.points} pathOptions={{ color: "#D7262E", weight: 4 }} />}
            {placed.map((i) => (
              <Marker key={i.id} position={[i.customer.lat, i.customer.lng]} icon={inspectionIcon(i.status)}>
                <Popup minWidth={250}>
                  <div dir="rtl" className="font-[Cairo] text-right" data-testid={`map-popup-${i.id}`}>
                    <div className="flex items-center justify-between gap-2"><span className="font-mono font-bold text-brand-navy">{i.ref}</span><StatusBadge status={i.status} /></div>
                    <p className="font-bold text-base mt-1">{i.customer.name}</p>
                    <p className="text-xs text-slate-500">{[i.customer.area, i.customer.address].filter(Boolean).join(" - ")}</p>
                    <p className="text-xs mt-1">المندوب: <b>{i.agent_name}</b> · {fmtDate(i.scheduled_at)}</p>
                    <Link to={`/admin/inspections/${i.id}`} className="text-xs text-brand-navy underline">فتح الكشف</Link>
                    {i.status !== "completed" && <AssignBox insp={i} agents={data.agents} onAssigned={load} />}
                  </div>
                </Popup>
              </Marker>
            ))}
            {located.map((a) => (
              <Marker key={a.id} position={[a.location.lat, a.location.lng]} icon={agentIcon(a.name, isStale(a.location))} zIndexOffset={-500}>
                <Popup>
                  <div dir="rtl" className="font-[Cairo] text-right" data-testid={`map-agent-popup-${a.id}`}>
                    <p className="font-bold">{a.name}</p>
                    <p className="text-xs">{a.region || "بدون منطقة"} · {a.active_tasks} مهام نشطة</p>
                    <p className="text-xs text-slate-500">آخر موقع: {fmtDate(a.location.updated_at)}{isStale(a.location) && " (قديم)"}</p>
                    <button onClick={() => showTrack(a)} data-testid={`map-track-${a.id}`} className="mt-1 text-xs font-bold text-brand-red underline">عرض مسار اليوم</button>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
        <aside className="space-y-4">
          <TrackPicker agents={data.agents} track={track} onShow={showTrack} onClear={() => setTrack(null)} />
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="map-agents-panel">
            <p className="font-heading font-bold mb-2 flex items-center gap-2"><Navigation size={16} className="text-brand-red" />المناديب</p>
            {data.agents.length === 0 && <p className="text-sm text-slate-500">لا يوجد مناديب نشطون</p>}
            {data.agents.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-1.5 text-sm border-b border-slate-100 last:border-0">
                <span className="font-semibold">{a.name}</span>
                <span className={`text-xs ${hasCoords(a.location) && !isStale(a.location) ? "text-emerald-700" : "text-slate-400"}`}>
                  {hasCoords(a.location) ? (isStale(a.location) ? "موقع قديم" : "متصل") : "بلا موقع"} · {a.active_tasks}
                </span>
              </div>
            ))}
            <p className="text-[11px] text-slate-500 mt-2">يظهر موقع المندوب عند فتحه للتطبيق والسماح بمشاركة الموقع.</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="map-unplaced-panel">
            <p className="font-heading font-bold mb-2 flex items-center gap-2"><MapPinOff size={16} className="text-slate-500" />بدون موقع على الخريطة ({unplaced.length})</p>
            {unplaced.slice(0, 20).map((i) => (
              <Link key={i.id} to={`/admin/inspections/${i.id}`} className="block py-1.5 text-sm border-b border-slate-100 last:border-0 hover:text-brand-navy" data-testid={`map-unplaced-${i.id}`}>
                <span className="font-mono text-xs text-slate-500">{i.ref}</span> {i.customer.name}
              </Link>
            ))}
            {unplaced.length > 0 && <p className="text-[11px] text-slate-500 mt-2">حدّد الموقع من «تعديل» داخل المعاينة.</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
